import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import useSWR from "swr";
import { Redirect } from "react-router-dom";

import "./index.scss";
import "../MerchantLoanRequests/styles/modalStyle.scss";
import { fetcher } from "../../utilities/_fetcher";
import BankInclusionListView from "./components/BankInclusionListView";
import BankInclusionSessionView from "./components/BankInclusionSessionView";

const BankInclusionRequests = () => {
  const [focusedLead, setFocusedLead] = useState(null);
  const [focusedLeadIndex, setFocusedLeadIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [counts, setCounts] = useState({
    total: 0,
    finance_pending: 0,
    cs_pending: 0,
    qc_l1: {
      pending: 0,
      executed: { count: 0, percentage: 0 },
      approved: { count: 0, percentage: 0 },
      rejected: { count: 0, percentage: 0 },
    },
    qc_l2: {
      pending: 0,
      executed: { count: 0, percentage: 0 },
      approved: { count: 0, percentage: 0 },
      sent_back: { count: 0, percentage: 0 },
    },
  });

  const prevFiltersRef = useRef({ activeTab, searchTerm });
  const filtersChangedRef = useRef(false);
  const initialLoadRef = useRef(true);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (data.length === 0) return;

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedLeadIndex((prev) => {
          const newIndex = prev <= 0 ? 0 : prev - 1;
          setFocusedLead(data[newIndex]);
          return newIndex;
        });
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedLeadIndex((prev) => {
          const newIndex = prev >= data.length - 1 ? data.length - 1 : prev + 1;
          setFocusedLead(data[newIndex]);
          return newIndex;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [data, focusedLeadIndex]);

  useEffect(() => {
    if (data.length > 0 && focusedLeadIndex === -1) {
      setFocusedLeadIndex(0);
      setFocusedLead(data[0]);
    } else if (data.length === 0) {
      setFocusedLeadIndex(-1);
      setFocusedLead(null);
    }
  }, [data, focusedLeadIndex]);

  const handleDetailsClick = useCallback(
    (bankInfoId) => {
      const request =
        data.find((req) => req.bank_info_id === bankInfoId) || null;
      const index = data.findIndex((req) => req.bank_info_id === bankInfoId);
      setFocusedLead(request);
      setFocusedLeadIndex(index);
    },
    [data]
  );
  const localUserType = JSON.parse(localStorage.getItem("currUserType"));

  const shouldCallApis =
    localUserType === "bkash-super-user" || localUserType === "cs_super_user";

  const apiUrl = useMemo(() => {
    if (!shouldCallApis) return null;

    const prevFilters = prevFiltersRef.current;
    const currentFilters = { activeTab, searchTerm };

    const hasFiltersChanged =
      prevFilters.activeTab !== activeTab ||
      prevFilters.searchTerm !== searchTerm;

    filtersChangedRef.current = hasFiltersChanged;

    prevFiltersRef.current = currentFilters;

    let apiUrl = `${process.env.REACT_APP_API_URL}/api/bank-inclusion/v1/qc-list`;
    const limit = 10;

    const pageToUse = hasFiltersChanged ? 1 : currentPage;

    const params = new URLSearchParams({
      page_no: pageToUse,
      page_size: limit,
    });

    if (hasFiltersChanged && currentPage !== 1) {
      setTimeout(() => {
        setCurrentPage(1);
        setPageCount(1);
        setFocusedLeadIndex(-1);
        setFocusedLead(null);
      }, 0);
    }

    if (activeTab && activeTab !== "all") {
      params.append("status", activeTab);
    }
    if (searchTerm) {
      params.append("search_query", searchTerm);
    }

    return `${apiUrl}?${params.toString()}`;
  }, [activeTab, searchTerm, currentPage, shouldCallApis]);

  const summaryApiUrl = shouldCallApis
    ? `${process.env.REACT_APP_API_URL}/api/bank-inclusion/v1/qc-summary`
    : null;

  const {
    data: response,
    error,
    mutate: mutateBankInclusionList,
  } = useSWR(apiUrl, fetcher, {
    fallbackData: {},
    onSuccess: (data) => {
      if (data.results) {
        setData(data.results);
        setPageCount(data.meta?.total_pages || 1);
        setCounts((prev) => ({
          ...prev,
          total: data.meta?.total_count || 0,
        }));
        if (!initialLoadRef.current && mutateSummary) {
          mutateSummary();
        }
        initialLoadRef.current = false;
      } else {
        setData([]);
        setPageCount(1);
        setCounts((prev) => ({
          ...prev,
          total: 0,
        }));
        initialLoadRef.current = false;
      }
    },
    onError: (err) => {
      setData([]);
      setPageCount(1);
      setCounts((prev) => ({
        ...prev,
        total: 0,
      }));
    },
    revalidateOnFocus: false,
    refreshInterval: 5 * 60 * 1000,
  });

  const {
    data: summaryResponse,
    error: summaryError,
    mutate: mutateSummary,
  } = useSWR(summaryApiUrl, fetcher, {
    onSuccess: (data) => {
      if (data) {
        setCounts((prev) => ({
          ...prev,
          qc_l1: data.qc_l1 || {
            pending: 0,
            executed: { count: 0, percentage: 0 },
            approved: { count: 0, percentage: 0 },
            rejected: { count: 0, percentage: 0 },
          },
          qc_l2: data.qc_l2 || {
            pending: 0,
            executed: { count: 0, percentage: 0 },
            approved: { count: 0, percentage: 0 },
            sent_back: { count: 0, percentage: 0 },
            rejected: { count: 0, percentage: 0 },
          },
        }));
      }
    },
    onError: () => {
      setCounts((prev) => ({
        ...prev,
        qc_l1: {
          pending: 0,
          executed: { count: 0, percentage: 0 },
          approved: { count: 0, percentage: 0 },
          rejected: { count: 0, percentage: 0 },
        },
        qc_l2: {
          pending: 0,
          executed: { count: 0, percentage: 0 },
          approved: { count: 0, percentage: 0 },
          sent_back: { count: 0, percentage: 0 },
          rejected: { count: 0, percentage: 0 },
        },
      }));
    },
    revalidateOnFocus: false,
    refreshInterval: 5 * 60 * 1000,
  });

  const localUserToken = JSON.parse(localStorage.getItem("currUserToken"));
  if (!localUserToken) return <Redirect to="/signin" />;

  const loading = shouldCallApis ? !response && !error : false;
  const summaryLoading = shouldCallApis
    ? !summaryResponse && !summaryError
    : false;

  return (
    <div className="main-container">
      {shouldCallApis ? (
        <BankInclusionListView
          data={data}
          loading={loading}
          summaryLoading={summaryLoading}
          counts={counts}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageCount={pageCount}
          focusedLead={focusedLead}
          setFocusedLead={setFocusedLead}
          focusedLeadIndex={focusedLeadIndex}
          setFocusedLeadIndex={setFocusedLeadIndex}
          handleDetailsClick={handleDetailsClick}
          mutateBankInclusionList={mutateBankInclusionList}
          mutateSummary={mutateSummary}
        />
      ) : (
        <BankInclusionSessionView />
      )}
    </div>
  );
};

export default BankInclusionRequests;
